import { OWNER_API_URL, NOTIFY_API_URL, PAYMENT_API_URL, CLIENT_API_URL } from '../constants/links';

const request = async (url, { fetchFn = fetch, method = 'GET', body, fallback, credentials } = {}) => {
  const response = await fetchFn(url, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    ...(credentials ? { credentials } : {}),
  });

  if (!response.ok) {
    throw new Error(fallback);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const loginRequest = (path, email, password) => request(`${OWNER_API_URL}${path}`, {
  method: 'POST',
  body: { email, password },
  fallback: 'Login failed. Please check your credentials.',
  // Required so the browser stores the ow_refresh_token cookie set by this
  // cross-origin response.
  credentials: 'include',
});

export const cognitoLogin = (email, password) => loginRequest('/auth/login', email, password);

// Same Cognito login, but the backend only accepts users flagged is_super_admin.
export const adminLogin = (email, password) => loginRequest('/admin/login', email, password);

export const cognitoRefreshToken = (email) => request(`${OWNER_API_URL}/auth/refresh`, {
  method: 'POST',
  body: { email },
  fallback: 'Failed to refresh token',
  // Required so the browser sends the ow_refresh_token cookie; the backend
  // falls back to it since no refresh_token is sent in the body anymore.
  credentials: 'include',
});

export const getUserProfile = (email, authenticatedFetch) => request(
  `${OWNER_API_URL}/users/${encodeURIComponent(email)}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load profile.' },
);

export const getUserRestaurants = (userId, authenticatedFetch) => request(
  `${OWNER_API_URL}/restaurants/user/${userId}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load restaurants.' },
);

export const getDictionaries = (authenticatedFetch) => request(
  `${OWNER_API_URL}/dictionaries`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load dictionaries.' },
);

export const getSalesSummary = (restaurantId, userId, from, to, authenticatedFetch) => request(
  `${CLIENT_API_URL}/orders/restaurant/${restaurantId}/report/summary?${new URLSearchParams({ user_id: userId, from, to })}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load the sales report.' },
);

export const getProductMix = (restaurantId, userId, from, to, authenticatedFetch) => request(
  `${CLIENT_API_URL}/orders/restaurant/${restaurantId}/report/products?${new URLSearchParams({ user_id: userId, from, to })}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load the product report.' },
);

export const getCompany = (companyId, authenticatedFetch) => request(
  `${OWNER_API_URL}/companies/${companyId}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load company.' },
);

export const submitContactForm = (formData) => request(`${OWNER_API_URL}/contact-forms/web`, {
  method: 'POST',
  body: formData,
  fallback: 'Failed to submit the form. Please try again later.',
});

export const sendContactFormNotification = (email, message, source = 'contact') => request(
  `${NOTIFY_API_URL}/notifications/contact-form`,
  {
    method: 'POST',
    body: {
      service: 'ow-owner',
      template_name: source === 'guide' ? 'contact_form_guide.html' : 'contact_form_contact.html',
      to_email: 'support@ow-client.com',
      subject: 'New Contact Form Submission',
      context: { email, message },
    },
    fallback: 'Failed to send the contact notification.',
  },
);

export const sendNotificationEmail = (payload, authenticatedFetch = null) => request(
  `${NOTIFY_API_URL}/notifications/email`,
  {
    fetchFn: authenticatedFetch || fetch,
    method: 'POST',
    body: payload,
    fallback: 'Failed to send email.',
  },
);

export const getRevolutConfig = (authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/revolut/config`,
  { fetchFn: authenticatedFetch, fallback: 'Revolut is not configured.' },
);

export const createRevolutSubscription = (payload, authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/revolut/subscription`,
  {
    fetchFn: authenticatedFetch,
    method: 'POST',
    body: payload,
    fallback: 'Failed to start Revolut payment.',
  },
);

export const createPayPalSubscription = (payload, authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/paypal/subscription`,
  {
    fetchFn: authenticatedFetch,
    method: 'POST',
    body: payload,
    fallback: 'Failed to start PayPal payment.',
  },
);

export const createJccSubscription = (payload, authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/jcc/subscription`,
  {
    fetchFn: authenticatedFetch,
    method: 'POST',
    body: payload,
    fallback: 'Failed to start card payment.',
  },
);

export const getJccSubscription = (orderId, authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/jcc/subscription/${encodeURIComponent(orderId)}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to verify card payment.' },
);

export const getPayPalSubscription = (orderId, authenticatedFetch) => request(
  `${PAYMENT_API_URL}/payments/paypal/subscription/${encodeURIComponent(orderId)}`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to verify PayPal payment.' },
);

export const getActiveRestaurants = (authenticatedFetch) => request(
  `${OWNER_API_URL}/restaurants/`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load active restaurants.' },
);

export const setRestaurantActive = (restaurantId, isActive, authenticatedFetch) => request(
  `${OWNER_API_URL}/restaurants/${restaurantId}/activate`,
  {
    fetchFn: authenticatedFetch,
    method: 'PUT',
    body: { is_active: isActive },
    fallback: `Failed to ${isActive ? 'activate' : 'deactivate'} the restaurant.`,
  },
);

export const getUsers = (authenticatedFetch) => request(
  `${OWNER_API_URL}/users/`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load users.' },
);

export const getInactiveRestaurants = (authenticatedFetch) => request(
  `${OWNER_API_URL}/restaurants/inactive_restaurants`,
  { fetchFn: authenticatedFetch, fallback: 'Failed to load inactive restaurants.' },
);
